<template>
  <Teleport to="#modals">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showBackground"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/50"
        />

        <!-- Modal Container -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <div
            v-if="showContent"
            :class="[
              'relative w-full max-h-[90vh] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden',
              modalSizeClass
            ]"
            @mousedown.stop=""
          >
            <!-- Header -->
            <div class="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-200 bg-white">
              <div class="flex items-center gap-3 min-w-0">
                <slot name="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"  class="w-5 h-5 text-slate-400 shrink-0">
    <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
</svg>
                </slot>

                <h2
                  :id="titleId"
                  class="text-lg font-semibold text-slate-900 truncate"
                >
                  <slot name="header">
                    {{ $t('Modal.defaultHeader') }}
                  </slot>
                </h2>

                <slot name="header-badge" />
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <slot name="header-actions" />

                <button
                  class="btn-icon btn-icon-red"
                  type="button"
                  aria-label="Close modal"
                  @click.prevent="emitClose"
                >
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
                </button>
              </div>
            </div>

            <!-- Body -->
            <div class="overflow-y-auto max-h-[calc(90vh-200px)] child:p-6">
              <slot name="body">
                {{ $t('Modal.defaultBody') }}
              </slot>
            </div>

            <div class="min-h-3 border-t border-slate-200 bg-white py-4 px-6">
              <slot name="footer">
                {{ $t('Modal.defaultFooter') }}
              </slot>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  }>(),
  {
    size: 'md',
  },
);

const emits = defineEmits<{
  close: [];
}>();

const titleId = computed(() => `modal-title-${Math.random().toString(36).substr(2, 9)}`);

// Size classes mapping
const sizeClasses = {
  sm: 'max-w-lg',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  '2xl': 'max-w-7xl',
  full: 'max-w-full mx-4',
};

const modalSizeClass = computed(() => sizeClasses[props.size]);
const showBackground = ref(false);
const showContent = ref(false);

onMounted(() => {
  showBackground.value = true;

  nextTick(() => {
    showContent.value = true;
  });
});

const closeModal = () => {
  showContent.value = false;

  nextTick(() => {
    showBackground.value = false;

    nextTick(() => {
      emits('close');
    });
  });
};

// Handle escape key
onKeyStroke('Escape', () => {
  closeModal();
});

// Emit close event
const emitClose = () => {
  closeModal();
};

// Expose methods for parent components
defineExpose({
  close: closeModal,
});
</script>
